import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import InterviewControls from "./InterviewControls";
import Typewriter from "typewriter-effect";
import micIcon from "../assets/mic_icon.png";
import sendIcon from "../assets/send_icon.png";
import { exitFullscreen } from "../utils/fullscreen";
import AvatarVisemePlayer from "./AvatarVisemePlayer";

/**
 * Layout base: vídeo + controles + legendas + input/mic.
 * Espera props para manter a lógica nos componentes finais.
 */
export default function InterviewLayout({
  controlsProps,
  videoProps,
  messages,
  showCaptions,
  isPlaying,
  isListening,
  isTranscribing,
  userInput,
  onInputChange,
  onSend,
  onToggleMic,
  quickOptions,
  quickActivationKey,
  onQuickSelect,
  quickDisabled = false,
  showTextInput = true,
  showMic = true,
  showReload = false,
  onReload,
  onRestartInterview,
  sendDisabled = false,
  layoutOptions = {},
  evidenceProps,
  progressText,
  progressPercent,
  progressTooltip,
  videoLoading = false,
  videoLoadingText = "Carregando video...",
  videoMissing = false,
  videoMissingText = "Video em fase de producao",
  children,
}) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isFullscreenActive, setIsFullscreenActive] = useState(false);
  const [hideQuickOptionsAfterAnswer, setHideQuickOptionsAfterAnswer] = useState(false);
  const [hasStartedCurrentQuestion, setHasStartedCurrentQuestion] = useState(false);
  const [quickVisibleForCurrentQuestion, setQuickVisibleForCurrentQuestion] = useState(false);
  const [showReloadMenu, setShowReloadMenu] = useState(false);
  const reloadMenuRef = useRef(null);
  const inputRef = useRef(null);
  const { key: videoKey, mode: videoMode, avatarId, mouthAssets, audioSrc, visemes, ...resolvedVideoProps } = videoProps || {};
  const opts = {
    pageWidth: layoutOptions.pageWidth || '500px',
    pageHeight: layoutOptions.pageHeight || '720px',
    videoWidth: layoutOptions.videoWidth || '1300px',
    messageMaxHeight: layoutOptions.messageMaxHeight || '260px',
    messageMarginBottom: layoutOptions.messageMarginBottom || '90px',
  };

  useEffect(() => {
    if (!window.visualViewport) return;
    const handleResize = () => {
      const viewportHeight = window.visualViewport.height;
      const keyboardOpen = window.innerHeight - viewportHeight > 80;
      if (!keyboardOpen) {
        setIsInputFocused(false);
      }
    };
    window.visualViewport.addEventListener('resize', handleResize);
    return () => window.visualViewport.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = window.document;
      const isFullscreen =
        !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement);
      setIsFullscreenActive(isFullscreen);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    handleFullscreenChange();
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!showReloadMenu) return;
    const handleOutsideClick = (event) => {
      if (!reloadMenuRef.current) return;
      if (!reloadMenuRef.current.contains(event.target)) {
        setShowReloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showReloadMenu]);

  useEffect(() => {
    if (!showReload) {
      setShowReloadMenu(false);
    }
  }, [showReload, quickActivationKey, videoKey]);



  useEffect(() => {
    if (isPlaying) {
      // Video em reprodução: exibir opções, mas bloqueadas (disabled via isPlaying).
      setHideQuickOptionsAfterAnswer(false);
      setHasStartedCurrentQuestion(true);
      setQuickVisibleForCurrentQuestion(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!Array.isArray(quickOptions) || quickOptions.length === 0) {
      setHideQuickOptionsAfterAnswer(false);
      setHasStartedCurrentQuestion(false);
      setQuickVisibleForCurrentQuestion(false);
    }
  }, [quickOptions]);

  useEffect(() => {
    // Nova pergunta/clipe: resetar gating visual dos botões rápidos.
    setHideQuickOptionsAfterAnswer(false);
    setHasStartedCurrentQuestion(false);
    setQuickVisibleForCurrentQuestion(false);
  }, [quickActivationKey, videoKey]);

  useEffect(() => {
    if (!Array.isArray(quickOptions) || quickOptions.length === 0) return;
    // Depois de iniciar ao menos uma vez, mantém visível:
    // - bloqueado durante o vídeo (isPlaying=true)
    // - habilitado ao fim (isPlaying=false && !quickDisabled)
    if (!isPlaying && hasStartedCurrentQuestion) {
      setQuickVisibleForCurrentQuestion(true);
    }
  }, [isPlaying, hasStartedCurrentQuestion, quickOptions]);

  const resizeInput = (el) => {
    if (!el) return;
    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 22;
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const maxHeight = lineHeight * 10 + paddingTop + paddingBottom;
    const minHeight = lineHeight + paddingTop + paddingBottom;
    el.style.height = 'auto';
    const nextHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    resizeInput(inputRef.current);
  }, [userInput]);

  const handleInputChange = (event) => {
    onInputChange?.(event);
    resizeInput(event.target);
  };

  const handleFullscreenToggle = () => {
    const doc = window.document;
    const docEl = doc.documentElement;
    const isFullscreen = doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement;
    if (isFullscreen) {
      exitFullscreen();
      return;
    }
    const request = docEl.requestFullscreen || docEl.webkitRequestFullscreen || docEl.msRequestFullscreen;
    if (request) {
      request.call(docEl);
      return;
    }
    const videoNode = videoProps?.ref?.current;
    if (videoNode?.webkitEnterFullscreen) {
      videoNode.webkitEnterFullscreen();
    }
  };

  const getQuickOptionStyle = () => ({
    borderColor: 'rgba(17, 24, 39, 0.6)',
    color: '#111827',
    backgroundColor: '#fff',
  });

  const handleQuickOptionClick = (value) => {
    if (quickDisabled || isPlaying) return;
    setHideQuickOptionsAfterAnswer(true);
    setQuickVisibleForCurrentQuestion(false);
    onQuickSelect?.(value);
  };

  const audioFeatureEnabled = controlsProps?.features?.audio !== false;
  const shouldRenderQuickContainer =
    Array.isArray(quickOptions) && quickOptions.length > 0 && !showTextInput;
  const shouldRenderInteractiveContainer =
    showTextInput || (audioFeatureEnabled && showMic) || shouldRenderQuickContainer;
  const currentVideoSpeed = Number(controlsProps?.videoSpeed);
  const safeVideoSpeed = Number.isFinite(currentVideoSpeed) && currentVideoSpeed > 0 ? currentVideoSpeed : 1;
  const captionTypeDelay = Math.max(18, Math.min(120, Math.round(55 / safeVideoSpeed)));
  return (
    <MainContainer>
      <PageContainer
        $pageWidth={opts.pageWidth}
        $pageHeight={opts.pageHeight}
        $videoWidth={opts.videoWidth}
      >
        <TopMenuRow>
          <InterviewControls {...controlsProps} />
          {evidenceProps && (
            <EvidenceButton
              type="button"
              data-tour="step-evidence"
              onClick={evidenceProps.onClick}
              title="Selecionar evidências"
            >
              📎
            </EvidenceButton>
          )}
          <FullscreenButton
            type="button"
            onClick={handleFullscreenToggle}
            title="Tela cheia"
            data-tour="step-fullscreen"
            $active={isFullscreenActive}
          >
            ⛶
          </FullscreenButton>
        </TopMenuRow>
        {null}

        {videoMode === 'viseme' ? (
          <AvatarVisemePlayer
            key={videoKey}
            avatarId={avatarId}
            mouthAssets={mouthAssets}
            audioSrc={audioSrc}
            visemes={visemes}
            {...resolvedVideoProps}
          />
        ) : (
          <video key={videoKey} {...resolvedVideoProps} controls={false} />
        )}
        {videoLoading && (
          <VideoLoadingOverlay>
            <VideoLoadingSpinner aria-hidden="true" />
            <VideoLoadingText>{videoLoadingText}</VideoLoadingText>
          </VideoLoadingOverlay>
        )}
        {!videoLoading && videoMissing && (
          <VideoLoadingOverlay>
            <VideoMissingBadge aria-hidden="true">!</VideoMissingBadge>
            <VideoLoadingText>{videoMissingText}</VideoLoadingText>
          </VideoLoadingOverlay>
        )}
        {showReload && (
          <ReloadMenuWrapper ref={reloadMenuRef}>
            <ReloadButton
              type="button"
              onClick={() => setShowReloadMenu((prev) => !prev)}
              title="Opcoes"
            >
              ↻
            </ReloadButton>
            {showReloadMenu && (
              <ReloadMenu>
                <ReloadMenuItem
                  type="button"
                  onClick={() => {
                    setShowReloadMenu(false);
                    onReload?.();
                  }}
                >
                  Repetir video
                </ReloadMenuItem>
                <ReloadMenuItem
                  type="button"
                  onClick={() => {
                    setShowReloadMenu(false);
                    onRestartInterview?.();
                  }}
                >
                  Reiniciar entrevista
                </ReloadMenuItem>
              </ReloadMenu>
            )}
          </ReloadMenuWrapper>
        )}

        <MessageContainer
          $messageMaxHeight={opts.messageMaxHeight}
          $messageMarginBottom={opts.messageMarginBottom}
        >
          {(() => {
            if (!messages.length) return null;
            let lastIaIndex = -1;
            if (showCaptions) {
              for (let i = messages.length - 1; i >= 0; i -= 1) {
                if (messages[i]?.type === "ia") {
                  lastIaIndex = i;
                  break;
                }
              }
            }
            return messages.map((msg, index) => {
              if (showCaptions) {
                if (msg.type !== "ia" || index !== lastIaIndex) return null;
              } else if (msg.type === "ia") {
                return null;
              }
              return (
                <MessageBox type={msg.type} key={index}>
                  <Message>
                    {msg.type === "ia" ? (
                      <Typewriter
                        key={`caption-${index}-${safeVideoSpeed}-${(msg.text || '').length}`}
                        onInit={(typewriter) => {
                          typewriter.typeString(msg.text || "").start();
                        }}
                        options={{ autoStart: true, loop: false, delay: captionTypeDelay, cursor: "" }}
                      />
                    ) : (
                      msg.text
                    )}
                  </Message>
                </MessageBox>
              );
            });
          })()}
        </MessageContainer>

        {shouldRenderInteractiveContainer && (
          <InteractiveContainer $isInputFocused={isInputFocused}>
            {!showTextInput && Array.isArray(quickOptions) && quickOptions.length > 0 && !hideQuickOptionsAfterAnswer && (
              <QuickOptionsContainer $insideControls $isYesNo={quickOptions.length === 2}>
                {quickOptions.map((opt) => (
                  <QuickOptionButton
                    key={opt.value}
                    type="button"
                    onClick={() => handleQuickOptionClick(opt.value)}
                    disabled={quickDisabled || isPlaying}
                    data-label={String(opt.label).toLowerCase()}
                    style={getQuickOptionStyle(opt.value)}
                  >
                    {opt.label}
                  </QuickOptionButton>
                ))}
              </QuickOptionsContainer>
            )}
            {showTextInput && (
              <form id="chat-input-form" onSubmit={onSend} autoComplete="off" data-lpignore="true" data-1p-ignore="true">
                <StyledInput
                  ref={inputRef}
                  data-tour="step-input"
                  rows={1}
                  name="interview_response"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  inputMode="text"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  placeholder={isPlaying ? "Aguarde..." : "Envie sua resposta"}
                  value={userInput}
                  onChange={handleInputChange}
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => setIsInputFocused(false)}
                  $isTranscribing={isTranscribing}
                />
              </form>
            )}
            <ActionButtons>
              {showTextInput && (
                <SendButton
                  form="chat-input-form"
                  type="submit"
                  disabled={sendDisabled}
                  data-tour="step-send"
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                >
                  <img src={sendIcon} alt="send" />
                </SendButton>
              )}
              {audioFeatureEnabled && showMic && (
                <MicButton
                  onClick={() => {
                    if (isPlaying) return;
                    onToggleMic?.();
                  }}
                  disabled={isPlaying}
                  $isListening={isListening}
                  data-tour="step-mic"
                >
                  {isListening ? (
                    <MicWave aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </MicWave>
                  ) : (
                    <img src={micIcon} alt="microphone" />
                  )}
                </MicButton>
              )}
            </ActionButtons>
          </InteractiveContainer>
        )}
        {showTextInput && Array.isArray(quickOptions) && quickOptions.length > 0 && !hideQuickOptionsAfterAnswer && (
          <QuickOptionsContainer $isYesNo={quickOptions.length === 2}>
            {quickOptions.map((opt) => (
              <QuickOptionButton
                key={opt.value}
                type="button"
                onClick={() => handleQuickOptionClick(opt.value)}
                disabled={quickDisabled || isPlaying}
                data-label={String(opt.label).toLowerCase()}
                style={getQuickOptionStyle(opt.value)}
              >
                {opt.label}
              </QuickOptionButton>
            ))}
          </QuickOptionsContainer>
        )}
      </PageContainer>
      {children ? <ChildrenWrapper>{children}</ChildrenWrapper> : null}
    </MainContainer>
  );
}

const VideoLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  background: linear-gradient(180deg, rgba(252, 254, 255, 0.72), rgba(252, 254, 255, 0.58));
  pointer-events: none;
`;

const VideoLoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(17, 24, 39, 0.2);
  border-top-color: rgba(17, 24, 39, 0.8);
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const VideoLoadingText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: rgba(17, 24, 39, 0.85);
`;

const VideoMissingBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(17, 24, 39, 0.75);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media only screen and (max-width: 480px) {
    width: 100vw;
    height: 100dvh;
    min-height: 100dvh;
  }
`;

const ChildrenWrapper = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const PageContainer = styled.div`
  --top-actions-width: 96px;
  width: ${props => props.$pageWidth};
  height: ${props => props.$pageHeight};
  background-color: #FCFEFF;
  box-shadow: 20px 20px 20px #BACCDE8A;
  border: 1px solid #D1D1D1;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  position: relative;
  align-items: flex-start;
  video {
    position: absolute;
    width: ${props => props.$videoWidth};
    border-radius: 16px;
    object-fit: cover;
    z-index: 1;
    pointer-events: none;
  }
  @media only screen and (max-width: 480px) {
    width: 100vw;
    height: 100dvh;
    min-width: 0;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    border: 0;
    video {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
  }
`;

const MessageContainer = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  margin-bottom: ${props => props.$messageMarginBottom};
  max-height: ${props => props.$messageMaxHeight};
  overflow-y: auto;
  width: 90%;
  z-index: 4;
  &::-webkit-scrollbar {
    width: 0;
    display: none;
  }
`;

const QuickOptionsContainer = styled.div`
  position: ${({ $insideControls }) => ($insideControls ? 'static' : 'absolute')};
  left: 22px;
  right: calc(22px + var(--controls-right-width) + 10px);
  width: ${({ $insideControls }) => ($insideControls ? '100%' : 'auto')};
  max-width: none;
  bottom: 90px;
  display: ${({ $isYesNo }) => ($isYesNo ? 'flex' : 'grid')};
  justify-content: ${({ $isYesNo }) => ($isYesNo ? 'center' : 'center')};
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${({ $isYesNo }) => ($isYesNo ? '14px' : '20px')};
  flex-wrap: ${({ $isYesNo }) => ($isYesNo ? 'nowrap' : 'initial')};
  margin: 0 auto;
  max-width: ${({ $isYesNo }) => ($isYesNo ? '320px' : '420px')};
  justify-items: center;
  z-index: 5;
  pointer-events: auto;

  @media only screen and (max-width: 480px) {
    left: 10px;
    width: calc(100% - (10px * 2) - var(--controls-right-width));
    bottom: 76px;
    gap: ${({ $isYesNo }) => ($isYesNo ? '12px' : '20px')};
    max-width: ${({ $isYesNo }) => ($isYesNo ? '280px' : '360px')};
  }
`;

const QuickOptionButton = styled.button`
  flex: 1;
  min-width: 0;
  min-height: 46px;
  padding: 0 12px;
  height: 46px;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  border: 2px solid rgba(17, 24, 39, 0.6);
  background: #fff;
  color: #111827;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
  transition: transform 0.1s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;

  &[data-label="sim"],
  &[data-label="não"],
  &[data-label="nao"] {
    aspect-ratio: auto;
    min-width: 96px;
    padding: 0 18px;
    flex: 0 1 140px;
  }

  &:hover {
    border-color: #1f2937;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media only screen and (max-width: 480px) {
    min-width: 44px;
    height: 38px;
    font-size: 14px;
    &[data-label="sim"],
    &[data-label="não"],
    &[data-label="nao"] {
      min-width: 82px;
      flex-basis: 120px;
    }
  }
`;

const MessageBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${(props) => (props.type === "ia" ? "flex-start" : "flex-end")};
`;

const Message = styled.div`
  font-family: "Roboto", sans-serif;
  font-size: 18px;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  height: auto;
  padding: 15px;
  border-radius: 12px;
  width: fit-content;
`;

const InteractiveContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 20px;
  z-index: 5;
  pointer-events: auto;
  --controls-left-width: 320px;
  --controls-right-width: 96px;

  form {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    margin-right: 10px;
  }

  @media only screen and (max-width: 480px) {
    left: 10px;
    right: 10px;
    bottom: ${({ $isInputFocused }) => ($isInputFocused ? 'auto' : '12px')};
    top: ${({ $isInputFocused }) =>
      $isInputFocused ? 'calc(64px + env(safe-area-inset-top))' : 'auto'};
    align-items: ${({ $isInputFocused }) => ($isInputFocused ? 'flex-start' : 'center')};
    justify-content: ${({ $isInputFocused }) => ($isInputFocused ? 'space-between' : 'flex-start')};

    form {
      flex: ${({ $isInputFocused }) =>
        $isInputFocused ? '0 0 min(320px, calc(100% - var(--controls-right-width)))' : '1 1 auto'};
      max-width: ${({ $isInputFocused }) =>
        $isInputFocused ? 'min(320px, calc(100% - var(--controls-right-width)))' : 'none'};
      align-items: ${({ $isInputFocused }) => ($isInputFocused ? 'flex-start' : 'center')};
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media only screen and (max-width: 480px) {
    width: var(--controls-right-width);
    justify-content: space-between;
    margin-left: auto;
  }
`;

const StyledInput = styled.textarea`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 38px;
  z-index: 6;
  border-radius: 12px;
  border: 2px solid #1f2937;
  background-color: #fff;
  color: #111827;
  padding: 8px 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  line-height: 20px;
  direction: ltr;
  text-align: left;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  resize: none;
  overflow-y: auto;
  align-self: flex-start;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
  }
  &::placeholder {
    color: #6b7280;
    font-weight: 500;
  }

  @media only screen and (max-width: 480px) {
    width: 200px;
    min-height: 34px;
    font-size: 16px;
    padding: 6px 10px;
    line-height: 18px;
  }
`;


const MicButton = styled.button`
  height: 52px;
  width: 52px;
  border-radius: 16px;
  border: none;
  background-color: rgba(0, 0, 0, 0.5);
  margin-left: 20px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  z-index: 5;

  img {
    filter: ${({ disabled }) => (disabled ? "grayscale(1) brightness(0.8)" : "none")};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }

  @media only screen and (max-width: 480px) {
    width: 44px;
    height: 44px;
    margin-left: 0;

    img {
      width: 24px;
      height: 24px;
    }
  }
`;

const MicWave = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    width: 4px;
    height: 14px;
    border-radius: 999px;
    background: #fff;
    display: inline-block;
    animation: micWave 1s ease-in-out infinite;
  }

  span:nth-child(2) {
    height: 20px;
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    height: 12px;
    animation-delay: 0.3s;
  }

  @keyframes micWave {
    0%, 100% {
      transform: scaleY(0.6);
      opacity: 0.7;
    }
    50% {
      transform: scaleY(1.2);
      opacity: 1;
    }
  }
`;

const SendButton = styled.button`
  height: 52px;
  width: 52px;
  border-radius: 16px;
  border: none;
  background-color: rgba(0, 0, 0, 0.5);
  margin-left: 10px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  z-index: 5;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  img {
    filter: ${({ disabled }) => (disabled ? "grayscale(1) brightness(0.8)" : "none")};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }

  @media only screen and (max-width: 480px) {
    width: 44px;
    height: 44px;
    margin-left: 0;

    img {
      width: 24px;
      height: 24px;
    }
  }
`;

const EvidenceButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: none;
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  pointer-events: auto;
  &:hover { background: rgba(0,0,0,0.85); }
`;

const TopMenuRow = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  z-index: 6;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const FullscreenButton = styled.button`
  display: flex;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: none;
  background: ${({ $active }) => ($active ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.5)')};
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  &:hover { background: rgba(0,0,0,0.85); }

  &:active,
  &:focus,
  &:focus-visible {
    background: rgba(0,0,0,0.5);
    outline: none;
  }
`;
const ReloadMenuWrapper = styled.div`
  position: absolute;
  z-index: 9;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ReloadMenu = styled.div`
  position: absolute;
  top: calc(100% + 18px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  min-width: 180px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(17, 24, 39, 0.95);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
`;

const ReloadMenuItem = styled.button`
  border: none;
  color: #fff;
  background: transparent;
  padding: 10px 12px;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ReloadButton = styled.button`
  position: absolute;
  z-index: 9;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);  transition: background 0.2s ease;  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;





















